import * as moment from "moment";
import axios, { AxiosError, AxiosResponse } from "axios";

import { CaseType } from "@teamprevent/common/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { Appointment } from "@teamprevent/common/calendar";
import { ClinicLocationTypeName } from "@teamprevent/common/appointment/models";
import { ResponseModel } from "@teamprevent/common/models/ResponseModel";
import { toast } from "@teamprevent/common/toasts";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { TaskType, parseTaskModel } from "../task/model";
import { BookAppointmentForm, BookingTasksModel } from "../appointmentMerging/models";

import {
    BookAppointmentTaskListModel,
    BookIntoClinicSuccessResult,
    BookIntoClinicFailResult,
    BookAdHocAppointmentResult,
    RebookAppointmentSuccessResult,
    AdHocCalendarAppointmentModel,
    RebookingReasonModel,
    BookAppointmentTaskListModelDto, RebookingAllModel, BookAdHocClinicAppointmentModel
} from "./models";

export class BookAppointmentsApi {
    private readonly apiUrl = "/api/bookappointments";

    public async get(
        customerIds: string[],
        taskTypes: string[],
        caseTypes: CaseType[],
        taskId: string,
        teamIds: string[]
    ): Promise<BookAppointmentTaskListModelDto> {
        const response = (await axios.get<ServerModelWithDateStrings<ResponseModel<BookAppointmentTaskListModel[]>>>(
            this.apiUrl,
            { params: { customerIds, taskTypes, caseTypes, taskId, teamIds } })).data;

        const bookAppointmentTasks = response.data;

        return {
            appointmentTasks: bookAppointmentTasks.map(task => this.parseModel(task)),
            data: {
                count: response.count
            }
        };
    }

    public async getBookTaskTypes(): Promise<TaskType[]> {
        const taskTypes = (await axios.get(`${this.apiUrl}/booktasktypes`)).data as TaskType[];
        return taskTypes;
    }

    public async bookIntoClinic(
        clinicId: string,
        ohCaseId: string,
        bookTaskId: string,
        appointmentStartTime: moment.Moment,
        appointmentEndTime: moment.Moment,
        description: string,
        locationType: ClinicLocationTypeName,
    ): Promise<BookIntoClinicSuccessResult | BookIntoClinicFailResult> {
        const startString = appointmentStartTime.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);
        const endString = appointmentEndTime.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);
        try {
            const response = await axios.post(
                `${this.apiUrl}/clinicappointment`,
                { clinicId, ohCaseId, bookTaskId, appointmentStartTime: startString, appointmentEndTime: endString, description, locationType });
            return {
                success: response.data.success,
                incorrectProfession: response.data.incorrectProfession,
                tasks: response.data.updatedTasks.map(parseTaskModel),
                ohCaseId,
                clinic: response.data.clinic
            };
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError.response && axiosError.response.status && axiosError.response.status === 400) {

                this.isProfessionIncorrect(axiosError);

                return { success: false };
            }

            throw axiosError;
        }
    }

    public async bookIntoOpenClinic(bookForm: BookAppointmentForm): Promise<BookIntoClinicSuccessResult | BookIntoClinicFailResult> {
        try {
            const start = bookForm.start.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);
            const end = bookForm.end.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);

            const response = await axios.post(`${this.apiUrl}/openClinicAppointments`, {
                ...bookForm,
                start,
                end
            });

            return {
                success: response.data.success,
                incorrectProfession: response.data.incorrectProfession,
                tasks: response.data.updatedTasks.map(parseTaskModel),
                clinic: response.data.clinic
            };
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError.response && axiosError.response.status && axiosError.response.status === 400) {
                this.isProfessionIncorrect(axiosError);
                this.isConflictingTime(axiosError);
                return { success: false };
            }

            throw axiosError;
        }
    }

    public async bookIntoMobileClinic(
        clinicId: string,
        ohCaseId: string,
        bookTaskId: string
    ): Promise<BookIntoClinicSuccessResult | BookIntoClinicFailResult> {
        try {
            const response = await axios.post(
                `${this.apiUrl}/mobileclinicappointment`,
                { clinicId, ohCaseId, bookTaskId });
            return {
                success: response.data.success,
                incorrectProfession: response.data.incorrectProfession,
                tasks: response.data.updatedTasks.map(parseTaskModel),
                ohCaseId,
                clinic: response.data.clinic
            };
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError.response && axiosError.response.status && axiosError.response.status === 400) {

                this.isProfessionIncorrect(axiosError);

                return { success: false };
            }

            throw axiosError;
        }
    }

    public async bookAdHocAppointment(
        ohCaseId: string,
        bookTaskId: string,
        appointment: AdHocCalendarAppointmentModel
    ): Promise<BookAdHocAppointmentResult> {
        const startString = appointment.start.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);
        const endString = appointment.end.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);
        const { teamPreventUsers, ...appointmentModel } = appointment;
        const data = {
            ...appointmentModel,
            ohCaseId,
            bookTaskId,
            appointmentStartTime: startString,
            appointmentEndTime: endString,
            clinicianIds: teamPreventUsers.map(u => u.id)
        };
        try {
            const result = await axios.post(`${this.apiUrl}/adhocappointment`, data);
            return {
                success: true,
                incorrectProfession: false,
                errors: { userClashIds: [] },
                appointment: result.data.appointment,
                updatedTasks: result.data.updatedTasks.map(parseTaskModel)
            };
        } catch (ex) {
            const axiosError = ex as AxiosError<{ userClashIds: Record<string, string>; incorrectProfession: string[] }>;
            if (axiosError.response && axiosError.response.status && axiosError.response.status === 400) {
                const userClashIds = { ...axiosError.response.data.userClashIds };

                this.isProfessionIncorrect(axiosError);

                return {
                    success: false,
                    incorrectProfession: axiosError.response.data.incorrectProfession?.length > 0,
                    errors: {
                        userClashIds: Object.keys(userClashIds)
                    },
                    appointment: null,
                    updatedTasks: null
                };
            }

            throw axiosError;
        }
    }

    public bookAdHocClinicAppointment = async (clinicId: string, appointment: AdHocCalendarAppointmentModel) => {
        const startString = appointment.start.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);
        const endString = appointment.end.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);
        const data: ServerModelWithDateStrings<BookAdHocClinicAppointmentModel> = {
            ...appointment,
            start: startString,
            end: endString,
            clinicId
        };

        return (await axios.post<ServerModelWithDateStrings<Appointment>,
            AxiosResponse<ServerModelWithDateStrings<Appointment>>,
            ServerModelWithDateStrings<BookAdHocClinicAppointmentModel>>(`${this.apiUrl}/adhocclinicappointment`, data)).data;
    };

    public async rebookAppointment(appoinmentTask: TaskModel, rebookingReasonModel: RebookingReasonModel): Promise<RebookAppointmentSuccessResult> {
        const result = await axios.post(`${this.apiUrl}/${appoinmentTask.caseId}/${appoinmentTask.id}/cancelandcreaterebooktask`, rebookingReasonModel);
        return {
            tasks: result.data.tasks.map(parseTaskModel),
            cancelledAppointmentId: result.data.cancelledAppointmentId,
            ohCaseId: appoinmentTask.caseId
        };
    }

    public async rebookAppointments(appointmentTasks: TaskModel[], rebookingReasonModel: RebookingReasonModel): Promise<RebookAppointmentSuccessResult[]> {
        const rebookingAllModel: RebookingAllModel = {
            rebookingReason: rebookingReasonModel.rebookingReason,
            rebookingReasonComment: rebookingReasonModel.rebookingReasonComment,
            appointmentTasks
        };
        const result = await axios.post(`${this.apiUrl}/cancelandcreaterebooktasks`, rebookingAllModel);

        return result.data.map((booking: RebookAppointmentSuccessResult) => {
            return {
                tasks: booking.tasks.map(parseTaskModel),
                cancelledAppointmentId: booking.cancelledAppointmentId,
                ohCaseId: booking.ohCaseId
            };
        });
    }

    public async chooseAndBook(ohCaseId: string, taskId: string): Promise<void> {
        await axios.post(`${this.apiUrl}/${ohCaseId}/${taskId}/chooseandbook`);
    }

    public async addBookingNotes(ohCaseId: string, taskId: string, notes: string): Promise<string> {
        const response = await axios.post<string>(`${this.apiUrl}/${ohCaseId}/${taskId}/notes`, { notes });
        return response.data;
    }

    public async getBookTasks(
        customerIds: string[],
        employeeId: string): Promise<BookingTasksModel> {

        const response = await axios.get<ServerModelWithDateStrings<BookingTasksModel>>(`${this.apiUrl}/bookingDetails`, {
            params: {
                customerIds,
                employeeId
            }
        });

        return {
            bookingTasks: response.data.bookingTasks.map(b => {
                return {
                    task: parseTaskModel(b.task),
                    locationType: b.locationType,
                    personalReferenceNumber: b.personalReferenceNumber
                };
            })
        };
    }

    private parseModel(task: ServerModelWithDateStrings<BookAppointmentTaskListModel>): BookAppointmentTaskListModel {
        return {
            ...task,
            referralCreated: task.referralCreated ? moment(task.referralCreated) : null,
            dueDate: task.dueDate ? moment(task.dueDate) : null
        };
    }

    private isProfessionIncorrect(axiosError: AxiosError<any>) {
        if (axiosError.response.data.hasOwnProperty("incorrectProfession")) { // eslint-disable-line no-prototype-builtins
            toast.error("Incorrect admin user profession for the book task type");
        }
    }

    private isConflictingTime(axiosError: AxiosError<any>) {
        if (axiosError.response.data.hasOwnProperty("timeConflict")) { // eslint-disable-line no-prototype-builtins
            toast.error(axiosError.response.data.timeConflict[0]);
        }
    }
}
